import React, { useEffect, useRef, useState } from 'react';
import { ArchetypeIllustration } from './ArchetypeIllustration';

// Kajabi opt-in form embed. Submitting applies the "[LM QUIZ] Birth Prep Quiz"
// tag and enrolls the person in the "Birth Prep Quiz – Archetype…" nurture sequence.
// The embed uses document.write, so it must live inside an iframe (srcdoc).
const KAJABI_EMBED_URL = 'https://www.karamears.com/forms/2149641991/embed.js';

// The srcdoc content: loads the Kajabi embed script and applies our brand styling
// to the resulting form. Kajabi's default title/subtitle are hidden because we
// have our own copy above the form.
const IFRAME_SRCDOC = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link href="https://fonts.googleapis.com/css2?family=Alegreya:ital,wght@0,400;0,500;1,400;1,500&display=swap" rel="stylesheet" />
<style>
  html, body {
    margin: 0;
    padding: 0;
    background: transparent;
    font-family: "Alegreya", Georgia, serif;
    color: #3D3830;
  }
  #kajabi-form { padding: 0 !important; }
  .kajabi-form--inline { background: transparent !important; }
  .kajabi-form__title,
  .kajabi-form__subtitle { display: none !important; }
  .kajabi-form__content { padding: 0 !important; }
  fieldset { border: none; padding: 0; margin: 0; }
  .kajabi-form__form-item { margin: 0 0 12px 0 !important; }
  .kajabi-form__form-item input,
  input[type="text"], input[type="email"] {
    background: rgba(255,255,255,0.7);
    border: 1px solid rgba(184, 167, 143, 0.65);
    border-radius: 14px;
    padding: 14px 16px;
    font-family: "Alegreya", Georgia, serif;
    font-size: 17px;
    color: #3D3830;
    width: 100%;
    outline: none;
    box-sizing: border-box;
    transition: border-color 200ms, background-color 200ms, box-shadow 200ms;
  }
  input::placeholder { color: rgba(61, 56, 48, 0.42); font-style: italic; }
  input:focus {
    border-color: #3D3830;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(61, 56, 48, 0.22);
  }
  .kajabi-form__btn,
  button[type="submit"] {
    background-color: #B8A78F !important;
    background: #B8A78F !important;
    background-image: none !important;
    color: #3D3830 !important;
    border-radius: 999px !important;
    padding: 16px 30px !important;
    font-family: "Alegreya", Georgia, serif !important;
    font-weight: 500 !important;
    letter-spacing: 0.10em !important;
    text-transform: uppercase !important;
    font-size: 13px !important;
    width: 100% !important;
    border: none !important;
    cursor: pointer !important;
    margin-top: 6px !important;
    transition: background-color 220ms ease, color 220ms ease, transform 220ms ease !important;
    box-shadow: none !important;
  }
  .kajabi-form__btn:hover,
  button[type="submit"]:hover {
    background-color: #3D3830 !important;
    background: #3D3830 !important;
    color: #ffffff !important;
    transform: translateY(-1px);
  }
  .kajabi-form__btn:disabled,
  button[type="submit"]:disabled { opacity: 0.55 !important; cursor: not-allowed !important; transform: none !important; }
  /* Parsley (validation) messages */
  .parsley-errors-list {
    margin: -6px 4px 8px 4px;
    padding: 0;
    list-style: none;
    font-size: 13px;
    color: #B14B4B;
    font-style: italic;
  }
</style>
</head>
<body>
<script src="${KAJABI_EMBED_URL}"></script>
</body>
</html>`;

export const RevealScreen = ({ archetype, onUnlock }) => {
  const iframeRef = useRef(null);
  const loadCountRef = useRef(0);
  const capturedNameRef = useRef('');
  const [submitting, setSubmitting] = useState(false);
  const [manualUnlockVisible, setManualUnlockVisible] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const wireInsideIframe = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const nameInput = doc.querySelector('input[name="form_submission[name]"]');
        const form = doc.querySelector('form');
        const submitBtn = doc.querySelector('button[type="submit"], .kajabi-form__btn');
        if (submitBtn) submitBtn.textContent = 'Show me my full result';
        if (!form) return;
        // On submit: grab the name value + show loading state on parent
        form.addEventListener('submit', () => {
          if (nameInput && nameInput.value.trim()) {
            capturedNameRef.current = nameInput.value.trim();
          }
          setSubmitting(true);
          // Fallback: if the iframe doesn't fire a second load within 6s,
          // show a manual "continue" button so the user is never stuck.
          setTimeout(() => setManualUnlockVisible(true), 6000);
        });
      } catch (err) {
        // Cross-origin or DOM not yet ready — ignored.
      }
    };

    const handleLoad = () => {
      loadCountRef.current += 1;
      if (loadCountRef.current === 1) {
        // Give Kajabi's document.write a moment, then wire submit listener
        setTimeout(wireInsideIframe, 250);
        setTimeout(wireInsideIframe, 1200); // second attempt if slow to render
      } else {
        // Second load = form was submitted and the iframe navigated to the
        // Kajabi confirmation URL. Unlock the archetype result.
        setTimeout(() => onUnlock(capturedNameRef.current), 500);
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [onUnlock]);

  const handleManualUnlock = () => onUnlock(capturedNameRef.current);

  return (
    <section
      data-testid="reveal-screen"
      className="screen-enter mx-auto w-full max-w-[640px] px-5 sm:px-8 pt-10 sm:pt-14 pb-14 text-center"
    >
      <div className="section-label mb-4" data-testid="reveal-eyebrow">Your archetype</div>

      <div
        className="mx-auto mb-7 inline-flex items-center justify-center rounded-full"
        style={{
          width: 104,
          height: 104,
          backgroundColor: '#B8A78F',
          boxShadow: 'none',
        }}
        data-testid="reveal-icon-bubble"
      >
        <ArchetypeIllustration archetypeKey={archetype.key} size={68} style={{ color: '#3D3830' }} />
      </div>

      <p className="font-body text-[13px] mb-2" style={{ color: '#3D3830', letterSpacing: '0.22em', textTransform: 'uppercase', opacity: 0.75 }}>
        You are
      </p>

      <h1
        data-testid="reveal-archetype-name"
        className="font-heading italic text-[44px] sm:text-[56px] leading-[1.05]"
        style={{ fontWeight: 500, color: '#3D3830' }}
      >
        {archetype.name}
      </h1>

      <p
        data-testid="reveal-tagline"
        className="font-heading italic text-[20px] sm:text-[22px] leading-[1.4] mt-5 max-w-[480px] mx-auto"
        style={{ color: 'rgba(61, 56, 48, 0.78)' }}
      >
        &ldquo;{archetype.tagline}&rdquo;
      </p>

      <div
        className="my-9 mx-auto w-12 h-px"
        style={{ backgroundColor: '#B8A78F' }}
        aria-hidden
      />

      <p
        data-testid="reveal-form-prompt"
        className="font-body text-[16px] sm:text-[17px] leading-[1.55] max-w-[460px] mx-auto"
        style={{ color: '#3D3830' }}
      >
        Enter your details below to discover your full report and preparation recommendations.
      </p>

      {/* Kajabi form embedded via srcdoc iframe */}
      <div className="mt-6 mx-auto max-w-[420px]" data-testid="reveal-form-wrap">
        <iframe
          ref={iframeRef}
          title="Kajabi email signup"
          srcDoc={IFRAME_SRCDOC}
          style={{
            width: '100%',
            border: 0,
            minHeight: 210,
            background: 'transparent',
            display: 'block',
          }}
          scrolling="no"
          data-testid="reveal-kajabi-iframe"
        />

        {submitting && !manualUnlockVisible && (
          <p
            data-testid="reveal-submitting-status"
            className="font-body italic text-[14px] mt-3"
            style={{ color: '#3D3830', opacity: 0.75 }}
          >
            One moment while we open your full result…
          </p>
        )}

        {manualUnlockVisible && (
          <div className="mt-4" data-testid="reveal-manual-continue">
            <p className="font-body text-[13px] mb-3" style={{ color: '#3D3830', opacity: 0.72 }}>
              Already submitted? Tap below to open your full report.
            </p>
            <button
              onClick={handleManualUnlock}
              className="btn-primary"
              data-testid="reveal-manual-continue-button"
            >
              Show me my full result
            </button>
          </div>
        )}

        <p className="font-body text-[12px] mt-4 text-center italic" style={{ color: '#3D3830', opacity: 0.7 }}>
          No spam, ever. Just your result and the occasional love note from Kara.
        </p>
      </div>
    </section>
  );
};
